import { audio } from '../../../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../../../ui/TouchTarget.js';
import { NUMBER_KINGDOM_ASSETS } from '../../../NumberKingdomAssets.js';
import {
  configureNumberKingdomDistrictRenderer,
  getNumberKingdomDistrictLayout,
} from '../../../components/NumberKingdomDistrictLayout.js';
import { createNumberKingdomHomeButton, type NumberKingdomHomeButton } from '../../../components/NumberKingdomHomeButton.js';
import {
  completeRegroupingWorkshop,
  discoverRegroupingWorkshop,
  getAdditionCitadelState,
  persistRegroupingWorkshopState,
  startRegroupingWorkshop,
} from '../AdditionCitadelState.js';
import {
  RegroupingWorkshopChallenge,
} from '../minigames/RegroupingWorkshopChallenge.js';
import { ADDITION_CITADEL_REGROUPING_1 } from '../minigames/RegroupingWorkshopConfig.js';
import {
  REGROUPING_WORKSHOP_FEEDBACK,
  type RegroupingWorkshopActionResult,
  type RegroupingWorkshopSerializedState,
  type RegroupingWorkshopSnapshot,
  type RegroupingWorkshopSource,
} from '../minigames/RegroupingWorkshopState.js';
import {
  REGROUPING_WORKSHOP_ASSETS,
  REGROUPING_WORKSHOP_ASSET_LIST,
  canEnterRegroupingWorkshop,
  getMissingRegroupingWorkshopImages,
} from './RegroupingWorkshopAssets.js';
import { canEnterSubtractionKeep } from '../../subtraction-keep/SubtractionKeepAssets.js';
import { SUBTRACTION_KEEP_SCENE_KEY } from '../../subtraction-keep/SubtractionKeepScene.js';

export const REGROUPING_WORKSHOP_SCENE_KEY = 'RegroupingWorkshop';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly action: () => void;
}

export class RegroupingWorkshopScene extends Phaser.Scene {
  static readonly requiredImageKeys = REGROUPING_WORKSHOP_ASSET_LIST.map(asset => asset.key);

  private challenge!: RegroupingWorkshopChallenge;
  private background!: Phaser.GameObjects.Image;
  private dormantWorkshop!: Phaser.GameObjects.Image;
  private poweredWorkshop!: Phaser.GameObjects.Image;
  private foreground!: Phaser.GameObjects.Image;
  private challengePanel!: Phaser.GameObjects.Image;
  private onesChamber!: Phaser.GameObjects.Image;
  private tensChamber!: Phaser.GameObjects.Image;
  private selectionTray!: Phaser.GameObjects.Image;
  private coreDormant!: Phaser.GameObjects.Image;
  private corePowered!: Phaser.GameObjects.Image;
  private selectionGlow!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private missionText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private sourceAText!: Phaser.GameObjects.Text;
  private sourceBText!: Phaser.GameObjects.Text;
  private onesCountText!: Phaser.GameObjects.Text;
  private tensCountText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeButton!: NumberKingdomHomeButton;
  private nextText!: Phaser.GameObjects.Text;
  private buttons: ArtButton[] = [];
  private dynamicPieces: Phaser.GameObjects.Image[] = [];
  private reducedMotion = false;
  private leaving = false;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: REGROUPING_WORKSHOP_SCENE_KEY }); }

  create() {
    this.buttons = [];
    this.dynamicPieces = [];
    this.leaving = false;

    const state = getAdditionCitadelState();
    const missing = getMissingRegroupingWorkshopImages(this.textures);
    if (!state.regroupingWorkshopEligible || !canEnterRegroupingWorkshop(this.textures)) {
      if (missing.length > 0) {
        console.error('[RegroupingWorkshop] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('NumberForge');
      return;
    }

    configureNumberKingdomDistrictRenderer(this);
    void audio.enterSoundscape('number-kingdom');
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.leaving = false;
    discoverRegroupingWorkshop();
    startRegroupingWorkshop(ADDITION_CITADEL_REGROUPING_1);
    SaveManager.setLastScene(REGROUPING_WORKSHOP_SCENE_KEY);
    SaveManager.save();

    this.buildEnvironment();
    this.buildInterface();
    const saved = state.regroupingWorkshopSnapshots[ADDITION_CITADEL_REGROUPING_1.id];
    this.challenge = new RegroupingWorkshopChallenge(
      ADDITION_CITADEL_REGROUPING_1,
      {
        onStateChanged: snapshot => {
          persistRegroupingWorkshopState(ADDITION_CITADEL_REGROUPING_1, this.challenge.serialize());
          this.refresh(snapshot);
        },
        onFeedback: result => this.presentFeedback(result),
        onComplete: (_config, snapshot) => this.handleComplete(snapshot),
      },
      saved && typeof saved === 'object' ? saved as RegroupingWorkshopSerializedState : undefined,
    );

    this.registerInput();
    this.registerLifecycle();
    this.layout();
    this.refresh(this.challenge.snapshot);
    this.cameras.main.fadeIn(this.reducedMotion ? 160 : 420, 255, 185, 90);
  }

  private buildEnvironment() {
    const assets = REGROUPING_WORKSHOP_ASSETS;
    this.background = this.add.image(640, 360, assets.workshopBackground.key)
      .setDisplaySize(1280, 720).setDepth(0);
    this.dormantWorkshop = this.add.image(640, 360, assets.workshopDormant.key)
      .setDisplaySize(1280, 720).setDepth(4);
    this.poweredWorkshop = this.add.image(640, 360, assets.workshopPowered.key)
      .setDisplaySize(1280, 720).setDepth(5).setAlpha(0);
    this.foreground = this.add.image(640, 360, assets.workshopForeground.key)
      .setDisplaySize(1280, 720).setDepth(80);
    this.challengePanel = this.add.image(640, 100, assets.challengePanel.key)
      .setDepth(20);
    this.onesChamber = this.add.image(465, 390, assets.onesChamber.key).setDepth(18);
    this.tensChamber = this.add.image(815, 390, assets.tensChamber.key).setDepth(18);
    this.selectionTray = this.add.image(640, 492, assets.selectionTray.key).setDepth(25);
    this.coreDormant = this.add.image(640, 336, assets.regroupCoreDormant.key).setDepth(32);
    this.corePowered = this.add.image(640, 336, assets.regroupCorePowered.key)
      .setDepth(33).setAlpha(0);
    this.selectionGlow = this.add.image(640, 492, assets.selectionGlow.key)
      .setDepth(34).setBlendMode(Phaser.BlendModes.ADD).setVisible(false);
    this.cosmo = this.add.image(112, 630, NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key)
      .setDepth(91);
  }

  private buildInterface() {
    this.titleText = this.makeText('REGROUPING WORKSHOP', 27, '#fff4bd', 'bold').setDepth(101);
    this.missionText = this.makeText('', 19, '#4a2d25', 'bold')
      .setWordWrapWidth(680).setLineSpacing(3).setDepth(102);
    this.feedbackText = this.makeText('', 16, '#fff8df', 'bold')
      .setStroke('#4a271d', 5).setWordWrapWidth(700).setDepth(102);
    this.sourceAText = this.makeText('', 17, '#fff4bd', 'bold')
      .setStroke('#4a271d', 5).setInteractive({ useHandCursor: true }).setDepth(103);
    this.sourceBText = this.makeText('', 17, '#fff4bd', 'bold')
      .setStroke('#4a271d', 5).setInteractive({ useHandCursor: true }).setDepth(103);
    this.onesCountText = this.makeText('', 16, '#fff8df', 'bold')
      .setStroke('#4a271d', 5).setDepth(103);
    this.tensCountText = this.makeText('', 16, '#fff8df', 'bold')
      .setStroke('#4a271d', 5).setDepth(103);
    this.controlsText = this.makeText(
      'A/B source  •  1–0 select  •  G regroup  •  T new ten  •  P power  •  H hint  •  R reset',
      13,
      '#fff8df',
      'bold',
    ).setStroke('#4a271d', 5).setDepth(103);
    this.backText = this.makeText('←  NUMBER FORGE', 16, '#ffffff', 'bold')
      .setStroke('#4a271d', 6).setInteractive({ useHandCursor: true }).setDepth(104);
    this.homeButton = createNumberKingdomHomeButton(this, this.returnHome);
    this.nextText = this.makeText('SUBTRACTION KEEP  →  (K)', 16, '#fff4bd', 'bold')
      .setStroke('#4a271d', 6).setInteractive({ useHandCursor: true }).setDepth(104).setVisible(false);

    [this.sourceAText, this.sourceBText, this.backText, this.nextText]
      .forEach(target => ensureMinimumTextTouchTarget(target));
    this.sourceAText.on('pointerup', () => this.activateSource('a'));
    this.sourceBText.on('pointerup', () => this.activateSource('b'));
    this.backText.on('pointerup', this.returnToForge, this);
    this.nextText.on('pointerup', this.travelToSubtractionKeep, this);

    this.buttons = [
      this.makeButton(REGROUPING_WORKSHOP_ASSETS.regroupButton.key, 'REGROUP 10', () => this.regroup()),
      this.makeButton(REGROUPING_WORKSHOP_ASSETS.powerButton.key, 'POWER', () => this.power()),
      this.makeButton(REGROUPING_WORKSHOP_ASSETS.hintButton.key, 'HINT', () => this.hint()),
      this.makeButton(REGROUPING_WORKSHOP_ASSETS.resetButton.key, 'RESET', () => this.reset()),
    ];
  }

  private makeText(value: string, size: number, color: string, style: string) {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_UI,
      fontSize: `${size}px`,
      fontStyle: style,
      color,
      align: 'center',
    }).setOrigin(0.5);
  }

  private makeButton(texture: string, label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, texture).setDepth(100).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 14, '#fff8df', 'bold').setStroke('#4a271d', 4).setDepth(101);
    image.on('pointerdown', () => image.setScale(image.scaleX * 0.96, image.scaleY * 0.96));
    image.on('pointerout', () => this.layout());
    image.on('pointerup', () => { this.layout(); action(); });
    return { image, label: text, action };
  }

  private activateSource(source: RegroupingWorkshopSource) {
    if (this.leaving) return;
    const snapshot = this.challenge.snapshot;
    if (!snapshot.revealedSources.includes(source)) {
      this.challenge.dispatch({ type: 'reveal-source', source });
      audio.click();
      return;
    }
    const remaining = source === 'a' ? snapshot.sourceARemaining : snapshot.sourceBRemaining;
    if (remaining.ones > 0) this.challenge.dispatch({ type: 'combine-one', source });
    else if (remaining.tens > 0) this.challenge.dispatch({ type: 'combine-ten', source });
    else this.feedbackText.setText('All pieces from that number are already in the chambers.');
  }

  private regroup() {
    const before = this.challenge.snapshot.regroupedTens;
    const result = this.challenge.dispatch({ type: 'regroup-selected-ones' });
    if (result === 'accepted' && this.challenge.snapshot.regroupedTens > before) {
      audio.correct();
      this.playRegroupReaction();
    }
  }

  private power() {
    const result = this.challenge.dispatch({ type: 'power-workshop' });
    if (result !== 'complete') audio.wrong();
  }

  private hint() {
    this.challenge.dispatch({ type: 'hint' });
    const hint = this.challenge.snapshot.hint;
    if (hint) this.feedbackText.setText(hint.message);
    audio.click();
  }

  private reset() {
    this.challenge.dispatch({ type: 'reset' });
    persistRegroupingWorkshopState(ADDITION_CITADEL_REGROUPING_1, this.challenge.serialize());
    this.feedbackText.setText('Workshop reset. Inspect both addends and build the total again.');
    audio.click();
  }

  private presentFeedback(result: RegroupingWorkshopActionResult) {
    if (result in REGROUPING_WORKSHOP_FEEDBACK) {
      this.feedbackText.setText(REGROUPING_WORKSHOP_FEEDBACK[
        result as keyof typeof REGROUPING_WORKSHOP_FEEDBACK
      ]);
      audio.wrong();
    }
  }

  private refresh(snapshot: RegroupingWorkshopSnapshot) {
    this.dynamicPieces.forEach(piece => piece.destroy());
    this.dynamicPieces = [];
    const aVisible = snapshot.revealedSources.includes('a');
    const bVisible = snapshot.revealedSources.includes('b');
    this.sourceAText.setText(aVisible
      ? `${ADDITION_CITADEL_REGROUPING_1.addendA}: ${snapshot.sourceARemaining.tens} tens, ${snapshot.sourceARemaining.ones} ones\nTap to move next piece`
      : `INSPECT ${ADDITION_CITADEL_REGROUPING_1.addendA}`);
    this.sourceBText.setText(bVisible
      ? `${ADDITION_CITADEL_REGROUPING_1.addendB}: ${snapshot.sourceBRemaining.tens} tens, ${snapshot.sourceBRemaining.ones} ones\nTap to move next piece`
      : `INSPECT ${ADDITION_CITADEL_REGROUPING_1.addendB}`);
    this.missionText.setText(snapshot.isComplete
      ? `${ADDITION_CITADEL_REGROUPING_1.addendA} + ${ADDITION_CITADEL_REGROUPING_1.addendB} = ${snapshot.finalNumber}. The workshop is powered!`
      : `Combine the ones. Select exactly 10 to make one new ten, then move every ten and power the workshop.`);
    this.onesCountText.setText(`ONES CHAMBER  ${snapshot.combinedOneIds.length}`);
    this.tensCountText.setText(`TENS CHAMBER  ${snapshot.combinedTens}`);
    this.selectionGlow.setVisible(snapshot.selectedOneIds.length === 10 && !snapshot.isComplete);
    this.coreDormant.setAlpha(snapshot.regroupedTens > 0 || snapshot.isComplete ? 0 : 1);
    this.corePowered.setAlpha(snapshot.regroupedTens > 0 || snapshot.isComplete ? 1 : 0);
    this.dormantWorkshop.setAlpha(snapshot.isComplete ? 0 : 1);
    this.poweredWorkshop.setAlpha(snapshot.isComplete ? 1 : 0);
    this.nextText.setVisible(snapshot.isComplete && canEnterSubtractionKeep(this.textures));
    this.renderCombinedOnes(snapshot);
    this.renderCombinedTens(snapshot);
    this.renderSourcePieces(snapshot);
    if (snapshot.regroupedTensAvailable > 0) this.renderNewTen();
    if (!snapshot.hint && !snapshot.isComplete && !this.feedbackText.text) {
      this.feedbackText.setText('The pieces remember your progress when you leave or resize.');
    }
    this.layout();
  }

  private renderCombinedOnes(snapshot: RegroupingWorkshopSnapshot) {
    snapshot.combinedOneIds.forEach((id, index) => {
      const column = index % 5;
      const row = Math.floor(index / 5);
      const selected = snapshot.selectedOneIds.includes(id);
      const piece = this.add.image(0, 0, REGROUPING_WORKSHOP_ASSETS.onePiece.key)
        .setDisplaySize(selected ? 48 : 44, selected ? 48 : 44)
        .setTint(selected ? 0xfff08a : 0xffffff)
        .setDepth(42)
        .setInteractive({ useHandCursor: true });
      piece.setData('slot', { kind: 'one', column, row });
      piece.on('pointerup', () => {
        this.challenge.dispatch({ type: 'toggle-one-selection', pieceId: id });
        audio.click();
      });
      this.dynamicPieces.push(piece);
    });
  }

  private renderCombinedTens(snapshot: RegroupingWorkshopSnapshot) {
    for (let index = 0; index < snapshot.combinedTens; index += 1) {
      const piece = this.add.image(0, 0, REGROUPING_WORKSHOP_ASSETS.tenPiece.key)
        .setDepth(40).setData('slot', { kind: 'combined-ten', index });
      this.dynamicPieces.push(piece);
    }
  }

  private renderSourcePieces(snapshot: RegroupingWorkshopSnapshot) {
    (['a', 'b'] as const).forEach(source => {
      if (!snapshot.revealedSources.includes(source)) return;
      const remaining = source === 'a' ? snapshot.sourceARemaining : snapshot.sourceBRemaining;
      for (let index = 0; index < remaining.ones; index += 1) {
        const piece = this.add.image(0, 0, REGROUPING_WORKSHOP_ASSETS.onePiece.key)
          .setDepth(42).setInteractive({ useHandCursor: true })
          .setData('slot', { kind: 'source-one', source, index });
        piece.on('pointerup', () => this.activateSource(source));
        this.dynamicPieces.push(piece);
      }
      for (let index = 0; index < remaining.tens; index += 1) {
        const piece = this.add.image(0, 0, REGROUPING_WORKSHOP_ASSETS.tenPiece.key)
          .setDepth(41).setInteractive({ useHandCursor: true })
          .setData('slot', { kind: 'source-ten', source, index });
        piece.on('pointerup', () => this.activateSource(source));
        this.dynamicPieces.push(piece);
      }
    });
  }

  private renderNewTen() {
    const piece = this.add.image(0, 0, REGROUPING_WORKSHOP_ASSETS.tenPiece.key)
      .setDepth(46).setTint(0xfff08a).setInteractive({ useHandCursor: true })
      .setData('slot', { kind: 'new-ten' });
    piece.on('pointerup', () => {
      const result = this.challenge.dispatch({ type: 'combine-ten', source: 'regrouped' });
      if (result === 'accepted' || result === 'ready') this.playTransferReaction();
    });
    this.dynamicPieces.push(piece);
  }

  private playRegroupReaction() {
    const effect = this.add.image(640, 340, REGROUPING_WORKSHOP_ASSETS.regroupTransformation.key)
      .setDisplaySize(230, 230).setDepth(75).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.72 : 0);
    if (this.reducedMotion) this.time.delayedCall(700, () => effect.destroy());
    else this.tweens.add({
      targets: effect,
      alpha: { from: 0, to: 0.9 },
      scaleX: 1.18,
      scaleY: 1.18,
      duration: 420,
      yoyo: true,
      hold: 140,
      onComplete: () => effect.destroy(),
    });
  }

  private playTransferReaction() {
    const effect = this.add.image(730, 390, REGROUPING_WORKSHOP_ASSETS.newTenTransfer.key)
      .setDisplaySize(300, 150).setDepth(74).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.68 : 0);
    if (this.reducedMotion) this.time.delayedCall(650, () => effect.destroy());
    else this.tweens.add({
      targets: effect,
      alpha: { from: 0, to: 0.82 },
      x: 815,
      duration: 520,
      yoyo: true,
      onComplete: () => effect.destroy(),
    });
  }

  private handleComplete(snapshot: RegroupingWorkshopSnapshot) {
    const result = completeRegroupingWorkshop(
      ADDITION_CITADEL_REGROUPING_1,
      this.challenge.serialize(),
    );
    this.feedbackText.setText('Ten ones became one ten. The regrouping workshop roars to life!');
    this.refresh(snapshot);
    audio.restoration();
    const burst = this.add.image(640, 360, REGROUPING_WORKSHOP_ASSETS.workshopPowerBurst.key)
      .setDisplaySize(440, 440).setDepth(78).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.72 : 0);
    if (this.reducedMotion) this.time.delayedCall(950, () => burst.destroy());
    else this.tweens.add({
      targets: burst,
      alpha: { from: 0, to: 0.92 },
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 650,
      yoyo: true,
      hold: 220,
      onComplete: () => burst.destroy(),
    });
    if (result.firstCompletion) audio.celebrate();
  }

  private registerInput() {
    this.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (this.leaving) return;
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnToForge();
    if (key === 'm') return this.returnHome();
    if (key === 'k') return this.travelToSubtractionKeep();
    if (key === 'a' || key === 'b') return this.activateSource(key);
    if (key === 'g') return this.regroup();
    if (key === 't') {
      this.challenge.dispatch({ type: 'combine-ten', source: 'regrouped' });
      return;
    }
    if (key === 'p' || key === 'enter') return this.power();
    if (key === 'h') return this.hint();
    if (key === 'r') return this.reset();
    if (/^[0-9]$/.test(key)) {
      const index = key === '0' ? 9 : Number(key) - 1;
      const pieceId = this.challenge.snapshot.combinedOneIds[index];
      if (pieceId) this.challenge.dispatch({ type: 'toggle-one-selection', pieceId });
    }
  }

  private layout() {
    const { safe, compact } = getNumberKingdomDistrictLayout(this);
    this.challenge?.layout({ left: safe.x, right: safe.right, top: safe.y, bottom: safe.bottom });
    const panelWidth = Math.min(compact ? 600 : 660, safe.width - 260);
    this.challengePanel.setPosition(safe.centerX, safe.y + (compact ? 86 : 92))
      .setDisplaySize(panelWidth, panelWidth * (420 / 960));
    this.titleText.setPosition(safe.centerX, safe.y + 28).setFontSize(compact ? 22 : 27);
    this.missionText.setPosition(safe.centerX, safe.y + (compact ? 92 : 98))
      .setFontSize(compact ? 16 : 19).setWordWrapWidth(panelWidth * 0.78);
    this.backText.setPosition(safe.x + (compact ? 250 : 282), safe.y + 30)
      .setFontSize(compact ? 13 : 16);
    this.homeButton.layout(safe, compact, 'left');
    this.nextText.setPosition(safe.right - (compact ? 128 : 150), safe.bottom - (compact ? 126 : 140))
      .setFontSize(compact ? 13 : 16);

    const chamberY = safe.y + (compact ? 350 : 370);
    const chamberW = compact ? 300 : 330;
    const chamberH = chamberW * (500 / 800);
    this.onesChamber.setPosition(safe.centerX - (compact ? 190 : 210), chamberY)
      .setDisplaySize(chamberW, chamberH);
    this.tensChamber.setPosition(safe.centerX + (compact ? 190 : 210), chamberY)
      .setDisplaySize(chamberW, chamberH);
    this.selectionTray.setPosition(safe.centerX - (compact ? 190 : 210), chamberY + 82)
      .setDisplaySize(compact ? 250 : 275, compact ? 100 : 110);
    this.selectionGlow.setPosition(this.selectionTray.x, this.selectionTray.y)
      .setDisplaySize(compact ? 266 : 292, compact ? 133 : 146);
    this.coreDormant.setPosition(safe.centerX, chamberY - 2).setDisplaySize(compact ? 150 : 172, compact ? 150 : 172);
    this.corePowered.setPosition(this.coreDormant.x, this.coreDormant.y)
      .setDisplaySize(this.coreDormant.displayWidth, this.coreDormant.displayHeight);
    this.sourceAText.setPosition(safe.x + (compact ? 125 : 150), safe.y + 145)
      .setFontSize(compact ? 14 : 17);
    this.sourceBText.setPosition(safe.right - (compact ? 125 : 150), safe.y + 145)
      .setFontSize(compact ? 14 : 17);
    this.onesCountText.setPosition(this.onesChamber.x, chamberY - chamberH * 0.42);
    this.tensCountText.setPosition(this.tensChamber.x, chamberY - chamberH * 0.42);

    const buttonY = safe.bottom - (compact ? 74 : 82);
    const buttonGap = compact ? 118 : 132;
    this.buttons.forEach((button, index) => {
      const x = safe.centerX + (index - 1.5) * buttonGap;
      button.image.setPosition(x, buttonY).setDisplaySize(compact ? 105 : 118, compact ? 53 : 59);
      button.label.setPosition(x, buttonY).setFontSize(compact ? 12 : 14);
    });
    this.feedbackText.setPosition(safe.centerX, safe.bottom - (compact ? 126 : 140))
      .setFontSize(compact ? 14 : 16).setWordWrapWidth(Math.min(700, safe.width - 260));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 22).setFontSize(compact ? 11 : 13);
    this.cosmo.setPosition(safe.x + (compact ? 62 : 78), safe.bottom - (compact ? 54 : 62))
      .setDisplaySize(compact ? 96 : 118, compact ? 86 : 105);
    this.positionDynamicPieces(safe.centerX, chamberY, compact);
  }

  private positionDynamicPieces(centerX: number, chamberY: number, compact: boolean) {
    const oneSize = compact ? 39 : 44;
    this.dynamicPieces.forEach(piece => {
      const slot = piece.getData('slot') as {
        kind: string;
        source?: 'a' | 'b';
        index?: number;
        column?: number;
        row?: number;
      };
      if (slot.kind === 'one') {
        piece.setPosition(
          centerX - (compact ? 190 : 210) - 80 + (slot.column ?? 0) * (oneSize + 2),
          chamberY + 45 + (slot.row ?? 0) * (oneSize - 2),
        ).setDisplaySize(piece.tintTopLeft === 0xfff08a ? oneSize + 4 : oneSize, piece.tintTopLeft === 0xfff08a ? oneSize + 4 : oneSize);
      } else if (slot.kind === 'combined-ten') {
        piece.setPosition(
          centerX + (compact ? 190 : 210) - 76 + (slot.index ?? 0) * (compact ? 31 : 35),
          chamberY + 45,
        ).setDisplaySize(compact ? 31 : 35, compact ? 58 : 66);
      } else if (slot.kind === 'new-ten') {
        piece.setPosition(centerX, chamberY + 95).setDisplaySize(compact ? 38 : 42, compact ? 66 : 74);
      } else {
        const sourceX = slot.source === 'a' ? centerX - (compact ? 410 : 445) : centerX + (compact ? 410 : 445);
        if (slot.kind === 'source-one') {
          piece.setPosition(sourceX - 38 + (slot.index ?? 0) * 24, chamberY - 78)
            .setDisplaySize(compact ? 32 : 36, compact ? 32 : 36);
        } else {
          piece.setPosition(sourceX - 34 + (slot.index ?? 0) * 28, chamberY - 20)
            .setDisplaySize(compact ? 27 : 31, compact ? 50 : 58);
        }
      }
    });
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => this.challenge?.setSuspended(true),
      onResume: () => this.challenge?.setSuspended(false),
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      configureNumberKingdomDistrictRenderer(this);
      this.layout();
    }, 80);
  };

  private readonly returnToForge = () => {
    if (this.leaving) return;
    this.leaving = true;
    SaveManager.save();
    this.scene.start('NumberForge');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly travelToSubtractionKeep = () => {
    if (this.leaving || !this.challenge.snapshot.isComplete || !canEnterSubtractionKeep(this.textures)) return;
    this.leaving = true;
    SaveManager.save();
    this.scene.start(SUBTRACTION_KEEP_SCENE_KEY);
  };

  private shutdown() {
    persistRegroupingWorkshopState(ADDITION_CITADEL_REGROUPING_1, this.challenge.serialize());
    this.unsubscribeLifecycle?.();
    this.unsubscribeLifecycle = undefined;
    audio.leaveSoundscape({ id: 'number-kingdom' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.dynamicPieces.forEach(piece => piece.destroy());
    this.dynamicPieces = [];
    this.tweens.killAll();
  }
}
